import { type MonetaryValue, add } from 'ezmoney';
import compact from 'lodash/compact';
import React from 'react';

import type { CostCenter } from 'modules/budgets/models/costCenter';
import { type PanelItemsSection } from 'src/core/common/components/Panel';
import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { type CustomFieldListItem } from '../../models/customFieldListItem';
import {
  type AnalyticalFieldAssociation,
  type Payable,
} from '../PayablePanelContainer';
import { PayableAccountingSectionDetailLine } from './PayableAccountingSectionDetailLine';

type Params = {
  costCenters: CostCenter[];
  customFields: CustomFieldListItem[];
  t: TGlobalFunctionTyped;
  hasCostCentersFeature: boolean;
  customFieldIdForExpenseCategory: string;
  showVATAccount: boolean;
};

type GroupedResults = Record<
  string,
  {
    label: string;
    deleted: boolean;
    type?:
      | 'costCenter'
      | 'customField'
      | 'expenseAccount'
      | 'taxAccount'
      | undefined;
    values: Record<
      string,
      {
        label: string;
        value: string | React.ReactElement;
        deleted: boolean;
        amount: MonetaryValue;
      }
    >;
  }
>;

const getCustomFieldValueInfo = (
  customField: CustomFieldListItem | undefined,
  fieldAssociation: AnalyticalFieldAssociation,
  automation: Payable['automation'],
  t: TGlobalFunctionTyped,
) => {
  const customFieldValue = customField?.custom_fields_values.find(
    ({ id }) => id === fieldAssociation.fieldEntityValueId,
  );

  let label: string = '';

  if (customField?.type === 'boolean') {
    if (customFieldValue !== undefined) {
      label =
        customFieldValue.value === '1' || customFieldValue.value === 'true'
          ? t('misc.yes')
          : t('misc.no');
    }
  } else {
    label = customFieldValue ? customFieldValue.value : '';
  }

  return {
    label,
    value: (
      <PayableAccountingSectionDetailLine
        automation={automation?.analyticalFieldAssociations?.find(
          (a) =>
            a.fieldKind === 'customField' &&
            a.fieldEntityId === customField?.id,
        )}
        translationKey={label}
        t={t}
      />
    ),
    deleted: !!customFieldValue?.deleted_at,
  };
};

const getItemValueInfo = (
  fieldAssociation: Payable['itemLines'][number]['analyticalFieldAssociations'][number],
  {
    costCenterById,
    customFieldById,
    automation,
    t,
  }: {
    t: TGlobalFunctionTyped;
    costCenterById: Map<string, CostCenter>;
    customFieldById: Map<string, CustomFieldListItem>;
    automation: Payable['automation'];
  },
) => {
  switch (fieldAssociation.fieldKind) {
    case 'costCenter': {
      const costCenter = costCenterById.get(
        fieldAssociation.fieldEntityValueId,
      );
      return {
        label: costCenter?.name || '',
        value: (
          <PayableAccountingSectionDetailLine
            automation={automation?.analyticalFieldAssociations?.find(
              (a) =>
                a.fieldKind === 'costCenter' &&
                a.fieldEntityValueId === costCenter?.id,
            )}
            translationKey={costCenter?.name || ''}
            t={t}
          />
        ),
        deleted: !!costCenter?.isDeleted,
      };
    }
    case 'customField': {
      const customField = customFieldById.get(fieldAssociation.fieldEntityId);

      if (customField) {
        return getCustomFieldValueInfo(
          customField,
          fieldAssociation,
          automation,
          t,
        );
      }
      return { label: '', value: '', deleted: false };
    }
    default:
      return { label: '', value: '', deleted: false };
  }
};

const addExpenseAccount = ({
  itemLine,
  accumulator,
  automation,
  t,
}: {
  itemLine: Payable['itemLines'][number];
  accumulator: GroupedResults;
  automation: Payable['automation'];
  t: TGlobalFunctionTyped;
}): GroupedResults => {
  const expenseAccountName = itemLine.expenseAccount?.name;
  const expenseAccountCode = itemLine.expenseAccount?.code;

  if (!expenseAccountName) {
    return { expenseAccount: accumulator.expenseAccount };
  }
  const existingExpenseAccountData =
    accumulator.expenseAccount?.values[expenseAccountName];

  return {
    expenseAccount: {
      ...accumulator.expenseAccount,
      values: {
        ...accumulator.expenseAccount?.values,
        [expenseAccountName]: {
          label: expenseAccountName,
          value: (
            <PayableAccountingSectionDetailLine
              {...{
                automation: automation?.expenseAccount,
                translationKey: expenseAccountCode
                  ? `${expenseAccountCode} - ${expenseAccountName}`
                  : expenseAccountName,
                t,
              }}
            />
          ),
          amount: existingExpenseAccountData
            ? add(existingExpenseAccountData.amount, itemLine.netAmount)
            : itemLine.netAmount,
          deleted: !!itemLine.expenseAccount?.isArchived,
        },
      },
    },
  };
};

const addVATAccount = ({
  itemLine,
  accumulator,
  automation,
  t,
}: {
  itemLine: Payable['itemLines'][number];
  accumulator: GroupedResults;
  automation: Payable['automation'];
  t: TGlobalFunctionTyped;
}): GroupedResults => {
  const name = itemLine.taxAccount?.name;
  if (!name) {
    return { taxAccount: accumulator.taxAccount };
  }
  const existingData = accumulator.taxAccount?.values[name];

  return {
    taxAccount: {
      ...accumulator.taxAccount,
      values: {
        ...accumulator.taxAccount?.values,
        [name]: {
          label: name,
          value: (
            <PayableAccountingSectionDetailLine
              {...{
                automation: automation?.vatAccount,
                translationKey: name,
                t,
              }}
            />
          ),
          amount: existingData
            ? add(existingData.amount, itemLine.taxAmount)
            : itemLine.taxAmount,
          deleted: !!itemLine.taxAccount?.isArchived,
        },
      },
    },
  };
};

const getGroupingKey = (
  analyticalFieldAssociation: AnalyticalFieldAssociation,
) => {
  switch (analyticalFieldAssociation.fieldKind) {
    case 'costCenter':
      return analyticalFieldAssociation.fieldKind;
    default:
      return analyticalFieldAssociation.fieldEntityId;
  }
};

const groupSplittableFields = (
  {
    payable,
    customFieldIdForExpenseCategory,
    ...params
  }: Params & { payable: Payable },
  initialData: GroupedResults,
  automation: Payable['automation'],
) => {
  const customFieldById = new Map(
    params.customFields.map((customField) => [customField.id, customField]),
  );

  const costCenterById = new Map(
    params.costCenters.map((costCenter) => [costCenter.id, costCenter]),
  );

  return payable.itemLines.reduce<GroupedResults>(
    (accumulator: GroupedResults, itemLine: Payable['itemLines'][number]) => {
      return {
        ...itemLine.analyticalFieldAssociations.reduce<GroupedResults>(
          (result: GroupedResults, analyticalFieldAssociation) => {
            const groupingKey = getGroupingKey(analyticalFieldAssociation);
            const existingResultForFieldEntity = result[groupingKey];
            const existingValueForFieldEntityValue =
              existingResultForFieldEntity?.values?.[
                analyticalFieldAssociation.fieldEntityValueId
              ];

            if (
              analyticalFieldAssociation.fieldKind === 'costCenter' &&
              costCenterById.size === 0
            ) {
              return {
                ...result,
                [groupingKey]: {
                  ...existingResultForFieldEntity,
                  values: {},
                },
              };
            }

            return {
              ...result,
              [groupingKey]: {
                ...existingResultForFieldEntity,
                values: {
                  ...existingResultForFieldEntity?.values,
                  [analyticalFieldAssociation.fieldEntityValueId]:
                    existingValueForFieldEntityValue
                      ? {
                          ...existingValueForFieldEntityValue,
                          amount: add(
                            existingValueForFieldEntityValue.amount,
                            itemLine.netAmount,
                          ),
                        }
                      : {
                          ...getItemValueInfo(analyticalFieldAssociation, {
                            ...params,
                            customFieldById,
                            costCenterById,
                            automation,
                          }),
                          amount: itemLine.netAmount,
                        },
                },
              },
            };
          },
          accumulator,
        ),
        ...addExpenseAccount({
          itemLine,
          accumulator,
          automation,
          t: params.t,
        }),
        ...addVATAccount({
          itemLine,
          accumulator,
          automation,
          t: params.t,
        }),
      };
    },
    initialData,
  );
};

const getInitialObjectWithAllFieldsForDisplay = ({
  t,
  hasCostCentersFeature,
  showVATAccount,
  customFieldIdForExpenseCategory,
  customFields,
}: Params) => {
  return {
    ...customFields.reduce((r: GroupedResults, customField) => {
      return {
        ...r,
        [customField.id]: {
          deleted: !!customField.deleted_at,
          label:
            customField.id === customFieldIdForExpenseCategory
              ? t('payables.panel.expenseCategory')
              : customField.name,
          values: {},
          type: 'customField' as const,
        },
      };
    }, {}),
    expenseAccount: {
      label: t('payables.panel.expenseAccount'),
      deleted: false,
      values: {},
      type: 'expenseAccount' as const,
    },
    taxAccount: {
      label: t('payables.panel.vatAccount'),
      values: {},
      deleted: !showVATAccount,
      type: 'taxAccount' as const,
    },
    costCenter: {
      label: t('payables.panel.costCenter'),
      values: {},
      deleted: !hasCostCentersFeature,
      type: 'costCenter' as const,
    },
  };
};

const orderPanelKeys = (
  currentKey: string,
  nextKey: string,
  itemOrderByLabel: string[],
) => {
  return (
    itemOrderByLabel.indexOf(currentKey) - itemOrderByLabel.indexOf(nextKey)
  );
};

const convertGroupedResultsToPanelItems = (
  groupedData: GroupedResults,
): (React.ComponentProps<typeof PanelItemsSection>['items'][number] & {
  label: string;
})[] => {
  return compact(
    Object.keys(groupedData).map((key) => {
      const { label, values, deleted, type } = groupedData[key];

      // We cannot have a li item without key (which is the label).
      // This can happen on re-renders, since the data here is re-rendered
      // on each of the data fetchers completions, it will be populated
      // eventually once the last data fetchers finish.
      if (!label) {
        return;
      }

      const valueKeys = Object.keys(values);
      const hasNoValue = valueKeys.length === 0;

      if (deleted && hasNoValue) {
        return;
      }

      const hasMoreThanOneValue = valueKeys.length > 1;
      if (hasMoreThanOneValue) {
        return {
          label,
          deleted,
          values: valueKeys.map((valueKey) => ({
            label: values[valueKey].label,
            deleted: !!values[valueKey].deleted,
            value: formatMonetaryValue(values[valueKey].amount),
          })),
          type,
        };
      }

      return {
        label: groupedData[key].label,
        value: values[valueKeys[0]]?.value || '-',
        deleted: !!deleted,
        valueDeleted: !!values[valueKeys[0]]?.deleted,
        type,
      };
    }),
  );
};

const buildSplittableFieldPanelItems = (
  params: Params & {
    payable: Payable;
    t: TGlobalFunctionTyped;
  },
): {
  label: string;
  value?: string | React.ReactElement;
  type?: string;
  expenseCategoryValueId?: string;
  deleted?: boolean;
}[] => {
  const { t } = params;
  const initialData = getInitialObjectWithAllFieldsForDisplay(params);
  const itemOrderByLabel = [
    t('payables.panel.expenseAccount'),
    t('payables.panel.vatAccount'),
    t('payables.panel.costCenter'),
    t('payables.panel.expenseCategory'),
    ...params.customFields
      .filter(({ id }) => id !== params.customFieldIdForExpenseCategory)
      .map(({ name }) => name)
      .sort(),
  ];

  const groupedData = groupSplittableFields(
    params,
    initialData,
    params.payable.automation,
  );

  return convertGroupedResultsToPanelItems(groupedData).sort((a, b) =>
    orderPanelKeys(a.label, b.label, itemOrderByLabel),
  );
};

export default buildSplittableFieldPanelItems;
